import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, MapPin, Calendar, Users, Star, Heart, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { experiencesData } from '../data/experiencesData';

// City tours data
const cityTours = [
  {
    id: 'kampala-walking',
    title: 'Walking Tour with Gaddafi Mosque',
    image: 'https://imgur.com/LTxFu0W.jpg',
    location: 'Kampala',
    duration: '3 hours',
    groupSize: '1-8 people',
    price: 32,
    category: 'City',
    rating: 4.8,
    reviews: 124,
    description: 'Explore Kampala\'s iconic mosque and cultural sites.',
    tag: 'Most Popular'
  },
  {
    id: 'kampala-complete',
    title: 'Complete Kampala Experience',
    image: 'https://imgur.com/EzHnSsj.jpg',
    location: 'Kampala',
    duration: 'Full Day',
    groupSize: '1-8 people',
    price: 25,
    category: 'City',
    rating: 4.9,
    reviews: 98,
    description: 'Comprehensive tour of Kampala\'s highlights.',
    tag: 'Best Value'
  },
  {
    id: 'kampala-food',
    title: 'City Walking Food Tour',
    image: 'https://imgur.com/4kwMnNu.jpg',
    location: 'Kampala',
    duration: '4 hours',
    groupSize: '2-8 people',
    price: 77,
    category: 'City',
    rating: 4.7,
    reviews: 76,
    description: 'Taste local delicacies and explore food markets.',
    tag: 'Food & Culture'
  },
  {
    id: 'jinja-rafting',
    title: 'White Water Rafting',
    image: 'https://imgur.com/tzBxU2P.jpg',
    location: 'Jinja',
    duration: 'Full Day',
    groupSize: '2-8 people',
    price: 160,
    category: 'City',
    rating: 4.9,
    reviews: 156,
    description: 'Experience thrilling Grade 5 rapids on the Nile.',
    tag: 'Water Adventure'
  },
  {
    id: 'jinja-bungee',
    title: 'Bungee Jumping',
    image: 'https://imgur.com/Z97JH4P.jpg',
    location: 'Jinja',
    duration: '2 hours',
    groupSize: '1-10 people',
    price: 120,
    category: 'City',
    rating: 4.8,
    reviews: 98,
    description: 'Take the plunge from a 44-meter platform over the Nile.',
    tag: 'Extreme Adventure'
  },
  {
    id: 'jinja-sunset',
    title: 'Sunset Cruise',
    image: 'https://imgur.com/ZhSOWHi.jpg',
    location: 'Jinja',
    duration: '2 hours',
    groupSize: '2-20 people',
    price: 25,
    category: 'City',
    rating: 4.7,
    reviews: 124,
    description: 'Relax on the Nile with drinks and stunning views.',
    tag: 'Relaxation'
  },
  {
    id: 'entebbe-day',
    title: '1-Day Entebbe Experience',
    image: 'https://imgur.com/ELruAkr.jpg',
    location: 'Entebbe',
    duration: 'Full Day',
    groupSize: '1-8 people',
    price: 196,
    category: 'City',
    rating: 4.8,
    reviews: 89,
    description: 'Explore Entebbe\'s attractions and Lake Victoria.',
    tag: 'Recommended'
  }
];

// Helper function to get the correct route path
const getRoutePath = (experience: any) => {
  // For city tours
  if (experience.category === 'City') {
    switch (experience.id) {
      case 'kampala-walking':
        return '/tours/kampala-walking';
      case 'kampala-complete':
        return '/tours/kampala-complete';
      case 'kampala-food':
        return '/tours/kampala-food';
      case 'jinja-rafting':
        return '/tours/jinja-rafting';
      case 'jinja-bungee':
        return '/tours/jinja-bungee';
      case 'jinja-sunset':
        return '/tours/jinja-sunset';
      case 'jinja-kayaking':
        return '/tours/jinja-kayaking';
      case 'jinja-quad-biking':
        return '/tours/jinja-quad-biking';
      case 'entebbe-day':
        return '/tours/entebbe-day';
      default:
        return `/tours/${experience.id}`;
    }
  }
  // For other experiences
  return `/experiences/${experience.id}`;
};

// Combine experiences data with city tours
const allExperiences = [...experiencesData, ...cityTours];

const UgandaTrips = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [durationFilter, setDurationFilter] = useState<string[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'All', label: t('experiences.categories.all') },
    { id: 'Wildlife', label: t('experiences.categories.wildlife') },
    { id: 'Safari', label: t('experiences.categories.safari') },
    { id: 'Cultural', label: t('experiences.categories.cultural') },
    { id: 'City', label: t('adventures.cityTours') },
    { id: 'Adventure', label: t('experiences.categories.adventure') }
  ];

  const filteredExperiences = allExperiences.filter(exp => {
    const matchesSearch = exp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         exp.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || exp.category === selectedCategory;
    const matchesPrice = exp.price >= priceRange[0] && exp.price <= priceRange[1];
    const matchesDuration = durationFilter.length === 0 || durationFilter.includes(exp.duration);
    
    return matchesSearch && matchesCategory && matchesPrice && matchesDuration;
  });

  const handleBookNow = (experience: any) => {
    setSelectedExperience(experience);
    setIsBookingModalOpen(true);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/TvdP6Mt.jpg"
            alt="Uganda landscape"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('pages.allExperiences.title')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.allExperiences.subtitle')}
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="sticky top-14 z-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('pages.allExperiences.filters')}
                {isFilterOpen ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </button>
              
              <div className="hidden md:flex space-x-2 overflow-x-auto">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      category.id === selectedCategory
                        ? 'bg-ta-green text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.priceRange')}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="5000"
                      step="100"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ta-green"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.duration')}</h3>
                  <div className="space-y-2">
                    {['1-3 hours', 'Half Day', 'Full Day', '2-3 Days', '4+ Days'].map((duration) => (
                      <label key={duration} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={durationFilter.includes(duration)}
                          onChange={() => {
                            if (durationFilter.includes(duration)) {
                              setDurationFilter(durationFilter.filter(d => d !== duration));
                            } else {
                              setDurationFilter([...durationFilter, duration]);
                            }
                          }}
                          className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                        />
                        <span className="ml-2 text-sm text-gray-700">{duration}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredExperiences.map((experience) => (
              <div 
                key={experience.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-40 sm:h-48">
                  <img 
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover"
                  />
                  <button className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-full hover:bg-white transition-colors">
                    <Heart className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600 hover:text-red-500" />
                  </button>
                  {experience.tag && (
                    <div className="absolute top-2 left-2 bg-ta-green text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                      {experience.tag}
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 bg-ta-green text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
                    {experience.category}
                  </div>
                </div>

                <div className="p-3 sm:p-4">
                  <div className="flex items-center mb-1">
                    <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs sm:text-sm font-medium">{experience.rating}</span>
                    <span className="mx-1 text-gray-400 text-xs">•</span>
                    <span className="text-xs text-gray-600">{experience.reviews} {t('common.reviews')}</span>
                  </div>

                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 line-clamp-2">
                    {experience.title}
                  </h3>

                  <div className="space-y-1 mb-3">
                    <div className="flex items-center text-xs text-gray-600">
                      <MapPin className="h-3 w-3 text-ta-green mr-1" />
                      {experience.location}
                    </div>
                    <div className="flex items-center text-xs text-gray-600">
                      <Calendar className="h-3 w-3 text-ta-green mr-1" />
                      {experience.duration}
                    </div>
                    <div className="flex items-center text-xs text-gray-600">
                      <Users className="h-3 w-3 text-ta-green mr-1" />
                      {experience.groupSize}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-500">{t('common.from')}</p>
                      <p className="text-sm sm:text-base font-bold text-gray-900">${experience.price}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Link
                        to={getRoutePath(experience)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        {t('experiences.viewDetails')}
                      </Link>
                      <button
                        onClick={() => handleBookNow(experience)}
                        className="px-2 py-1 text-xs bg-ta-green text-white rounded hover:bg-ta-green-dark transition-colors"
                      >
                        {t('common.book')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedExperience && (
        <BookingModal
          experience={selectedExperience}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedExperience(null);
          }}
        />
      )}

      <Footer />
    </>
  );
};

export default UgandaTrips;